// -----------------------------------------------------------------------------
// PoolQueuePill — small nav-bar pill that shows how many PO pool bags are still
// awaiting split into physical bags. Clicking it lands the operator on the
// Inventory page which already defaults to the "Pending Pool Bags" filter.
// Auto-refreshes every 60s so a freshly received PO shows up without a reload.
// -----------------------------------------------------------------------------
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Package } from "@phosphor-icons/react";
import { api } from "../lib/api";

const POLL_MS = 60_000;

export default function PoolQueuePill() {
  const [status, setStatus] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await api.get("/inventory/pool-queue-status");
        if (!cancelled) setStatus(r.data);
      } catch {
        if (!cancelled) setStatus(null);
      }
    };
    load();
    const t = setInterval(load, POLL_MS);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const count = status?.count || 0;
  const escalated = !!status?.escalated;
  if (!count) return null;

  const tone = escalated
    ? "bg-red-500/25 hover:bg-red-500/40 text-red-100 ring-red-400/50 animate-pulse"
    : "bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 ring-amber-500/30";
  const titleTxt = escalated
    ? `⚠ Queue escalated · ${count} pool bag(s) · oldest ${status?.oldest_days}d (limit ${status?.max_age_days}d, max ${status?.max_count})`
    : `${count} PO pool bag${count === 1 ? "" : "s"} still awaiting split into physical bags`;

  return (
    <button
      type="button"
      onClick={() => nav("/inventory")}
      data-testid="pool-queue-pill"
      data-escalated={escalated ? "1" : "0"}
      className={`hidden md:flex items-center gap-1.5 h-9 px-3 rounded-xl ring-1 transition-all duration-200 active:scale-95 text-xs font-mono uppercase tracking-wider whitespace-nowrap ${tone}`}
      title={titleTxt}
    >
      <Package size={14} weight="fill" />
      <span className="font-bold">{count}</span>
      <span className="hidden lg:inline">pool bag{count === 1 ? "" : "s"}</span>
      {escalated && <span className="hidden lg:inline">⚠</span>}
    </button>
  );
}
